import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useMockRaffleTicketWiggledSubscription,
  useRaffleTicketWiggledSubscription,
} from '@/generated/graphql';
import { useMockRealtimeChannel } from '@/hooks/useMockRealtimeChannel';
import { RAFFLE_WIGGLE_MS } from './ticketMotion';

function useWigglePlayback() {
  const [playingById, setPlayingById] = useState<Record<string, boolean>>({});
  const timersRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach((timer) => window.clearTimeout(timer));
    };
  }, []);

  const play = useCallback((submissionId?: string | null) => {
    if (!submissionId) {
      return;
    }
    const existing = timersRef.current[submissionId];
    if (existing) {
      window.clearTimeout(existing);
    }
    setPlayingById((prev) => ({ ...prev, [submissionId]: false }));
    requestAnimationFrame(() => {
      setPlayingById((prev) => ({ ...prev, [submissionId]: true }));
      timersRef.current[submissionId] = window.setTimeout(() => {
        setPlayingById((prev) => ({ ...prev, [submissionId]: false }));
        delete timersRef.current[submissionId];
      }, RAFFLE_WIGGLE_MS);
    });
  }, []);

  return { playingById, play };
}

/**
 * Stage listens for Fan ticket taps and replays a short wiggle on the
 * matching card. Live Stage uses the live channel; preview also watches mock.
 */
export function useRaffleTicketWiggle(engagementId: string) {
  const mockRealtime = useMockRealtimeChannel();
  const { playingById, play } = useWigglePlayback();

  useRaffleTicketWiggledSubscription({
    variables: { engagementId },
    onData: ({ data }) => {
      play(data.data?.raffleTicketWiggled?.submissionId);
    },
  });
  useMockRaffleTicketWiggledSubscription({
    skip: !mockRealtime,
    variables: { engagementId },
    onData: ({ data }) => {
      play(data.data?.mockRaffleTicketWiggled?.submissionId);
    },
  });

  return playingById;
}

/** Local one-shot wiggle for the Fan ticket tap target. */
export function useLocalRaffleTicketWiggle() {
  const { playingById, play } = useWigglePlayback();
  return {
    wiggling: Boolean(playingById.local),
    playWiggle: () => play('local'),
  };
}
